import { Action, ActionReducer, createFeatureSelector, createReducer, createSelector, MemoizedSelector, on } from '@ngrx/store';
import * as PurchaseActivityActions from './purchase-activity.actions';
import {
  IPurchaseActivityItem,
  IPurchaseActivityList,
  IPurchaseActivityPaginationResponse,
} from '../purchase-activity.models';
import { EPurchaseActivityPreviousOrdersTabs, EPurchaseActivityTabs } from '../purchase-activity.configurations';

const initialPagination: IPurchaseActivityPaginationResponse = {
  currentPage: 0,
  previousPage: 0,
  nextPage: 0,
  lastPage: 0,
};

export interface IPurchaseActivityState {
  previousOrders: IPurchaseActivityList;
  archiveOrders: IPurchaseActivityList;
  pendingOrders: IPurchaseActivityList;
  previousQuotes: IPurchaseActivityList;
  previousRequests: IPurchaseActivityList;
  purchaseActivityDetails: IPurchaseActivityItem | null;
  isPurchaseActivityDetailsLoading: boolean;
}

type PurchaseActivityStateKey =
  'previousOrders' |
  'archiveOrders' |
  'pendingOrders' |
  'previousQuotes' |
  'previousRequests';

export const initialState: IPurchaseActivityState = {
  previousOrders: {
    itemsPerPage: [],
    pagination: {...initialPagination},
    isLoaded: false,
  },
  archiveOrders: {
    itemsPerPage: [],
    pagination: {...initialPagination},
    isLoaded: false,
  },
  pendingOrders: {
    itemsPerPage: [],
    pagination: {...initialPagination},
    isLoaded: false,
  },
  previousQuotes: {
    itemsPerPage: [],
    pagination: {...initialPagination},
    isLoaded: false,
  },
  previousRequests: {
    itemsPerPage: [],
    pagination: {...initialPagination},
    isLoaded: false,
  },
  purchaseActivityDetails: null,
  isPurchaseActivityDetailsLoading: false,
};

export const reducer: ActionReducer<IPurchaseActivityState, Action> = createReducer(
  initialState,

  // *** Purchase Activity Details ***
  on(PurchaseActivityActions.setPurchaseActivityDetails, (state, {purchaseActivityDetails}) => ({
    ...state,
    purchaseActivityDetails,
  })),
  on(PurchaseActivityActions.loadPurchaseActivityDetails, (state) => ({
    ...state,
    purchaseActivityDetails: null,
    isPurchaseActivityDetailsLoading: true,
  })),
  on(PurchaseActivityActions.loadPurchaseActivityDetailsSuccess, (state, {purchaseActivityDetails}) => ({
    ...state,
    purchaseActivityDetails: purchaseActivityDetails.data[0],
    isPurchaseActivityDetailsLoading: false,
  })),
  on(PurchaseActivityActions.loadPurchaseActivityDetailsFailure, (state) => ({
    ...state,
    isPurchaseActivityDetailsLoading: false,
  })),
  // *** Previous Orders ***
  on(PurchaseActivityActions.loadOrdersInPreviousOrdersSuccess, (state, {orders}) => {
    const itemsPerPage: IPurchaseActivityItem[][] = [...state.previousOrders.itemsPerPage];
    itemsPerPage[orders.pagination.currentPage] = orders.data;

    return {
      ...state,
      previousOrders: {
        ...state.previousOrders,
        itemsPerPage,
        pagination: orders.pagination,
        isLoaded: true,
      },
    };
  }),
  on(PurchaseActivityActions.loadOrdersInPreviousOrdersFailure, (state) => ({
    ...state,
    previousOrders: {
      ...state.previousOrders,
      isLoaded: true,
    },
  })),

  // *** Archive Orders ***
  on(PurchaseActivityActions.loadArchiveInPreviousOrdersSuccess, (state, {archiveOrders}) => {
    const itemsPerPage: IPurchaseActivityItem[][] = [...state.archiveOrders.itemsPerPage];
    itemsPerPage[archiveOrders.pagination.currentPage] = archiveOrders.data;

    return {
      ...state,
      archiveOrders: {
        ...state.archiveOrders,
        itemsPerPage,
        pagination: archiveOrders.pagination,
        isLoaded: true,
      },
    };
  }),
  on(PurchaseActivityActions.loadArchiveInPreviousOrdersFailure, (state) => ({
    ...state,
    archiveOrders: {
      ...state.archiveOrders,
      isLoaded: true,
    },
  })),

  // *** Pending Orders ***
  on(PurchaseActivityActions.loadOrdersInPendingOrdersSuccess, (state, {pendingOrders}) => {
    const itemsPerPage: IPurchaseActivityItem[][] = [...state.pendingOrders.itemsPerPage];
    itemsPerPage[pendingOrders.pagination.currentPage] = pendingOrders.data;

    return {
      ...state,
      pendingOrders: {
        ...state.pendingOrders,
        itemsPerPage,
        pagination: pendingOrders.pagination,
        isLoaded: true,
      },
    };
  }),
  on(PurchaseActivityActions.loadOrdersInPendingOrdersFailure, (state) => ({
    ...state,
    pendingOrders: {
      ...state.pendingOrders,
      isLoaded: true,
    },
  })),

  // *** Previous Quotes ***
  on(PurchaseActivityActions.loadQuotesInPreviousQuotesSuccess, (state, {previousQuotes}) => {
    const itemsPerPage: IPurchaseActivityItem[][] = [...state.previousQuotes.itemsPerPage];
    itemsPerPage[previousQuotes.pagination.currentPage] = previousQuotes.data;

    return {
      ...state,
      previousQuotes: {
        ...state.previousQuotes,
        itemsPerPage,
        pagination: previousQuotes.pagination,
        isLoaded: true,
      },
    };
  }),
  on(PurchaseActivityActions.loadQuotesInPreviousQuotesFailure, (state) => ({
    ...state,
    previousQuotes: {
      ...state.previousQuotes,
      isLoaded: true,
    },
  })),

  // *** Previous Requests ***
  on(PurchaseActivityActions.loadRequestsInPreviousRequestsSuccess, (state, {previousRequests}) => {
    const itemsPerPage: IPurchaseActivityItem[][] = [...state.previousRequests.itemsPerPage];
    itemsPerPage[previousRequests.pagination.currentPage] = previousRequests.data;

    return {
      ...state,
      previousRequests: {
        ...state.previousRequests,
        itemsPerPage,
        pagination: previousRequests.pagination,
        isLoaded: true,
      },
    };
  }),
  on(PurchaseActivityActions.loadRequestsInPreviousRequestsFailure, (state) => ({
    ...state,
    previousRequests: {
      ...state.previousRequests,
      isLoaded: true,
    },
  })),

  on(PurchaseActivityActions.updatePreviousRequestVersionStatus, (state, {tab, page, orderId, newStatus}) => {
    const stateKey: PurchaseActivityStateKey | null = getStateKeyFromTab(tab);
    if (!stateKey) {
      return state;
    }

    const currentSection: IPurchaseActivityList = state[stateKey];
    const itemsPerPage: IPurchaseActivityItem[][] = [...currentSection.itemsPerPage];
    const currentPageItems: IPurchaseActivityItem[] = itemsPerPage[page] ?? [];
    itemsPerPage[page] = currentPageItems.map((item: IPurchaseActivityItem) => {
      if (item.id === orderId && item.attributes.shownVersion) {
        return {
          ...item,
          attributes: {
            ...item.attributes,
            shownVersion: {
              ...item.attributes.shownVersion,
              status: newStatus,
            },
          },
        };
      }
      return item;
    });
    return {
      ...state,
      [stateKey]: {
        ...currentSection,
        itemsPerPage,
      },
    };
  }),

  // *** Pagination ***
  on(PurchaseActivityActions.changePagination, (state, {tab, page}) => {
    const stateKey: PurchaseActivityStateKey | null = getStateKeyFromTab(tab);
    if (!stateKey) {
      return state;
    }

    const currentSection: IPurchaseActivityList = state[stateKey];

    return {
      ...state,
      [stateKey]: {
        ...currentSection,
        pagination: {
          ...currentSection.pagination,
          currentPage: page,
        },
      },
    };
  }),

  // *** Set Is Loaded ***
  on(PurchaseActivityActions.setIsLoadedForTab, (state, {tab, isLoaded}) => {
    const stateKey: PurchaseActivityStateKey | null = getStateKeyFromTab(tab);
    if (!stateKey) {
      return state;
    }

    const currentSection: IPurchaseActivityList = state[stateKey];

    return {
      ...state,
      [stateKey]: {
        ...currentSection,
        isLoaded: isLoaded,
      },
    };
  }),
  on(PurchaseActivityActions.setIsLoadedForItem, (state, {tab, page, orderId, isLoaded}) => {
    const stateKey: PurchaseActivityStateKey | null = getStateKeyFromTab(tab);
    if (!stateKey) {
      return state;
    }

    const currentSection: IPurchaseActivityList = state[stateKey];
    const itemsPerPage: IPurchaseActivityItem[][] = [...currentSection.itemsPerPage];
    const currentPageItems: IPurchaseActivityItem[] = itemsPerPage[page] ?? [];
    itemsPerPage[page] = currentPageItems.map((item: IPurchaseActivityItem) => {
      if (item.id === orderId) {
        return {
          ...item,
          attributes: {
            ...item.attributes,
            isLoaded: isLoaded,
          },
        };
      }
      return item;
    });
    return {
      ...state,
      [stateKey]: {
        ...currentSection,
        itemsPerPage,
      },
    };
  }),

  // *** Clear Purchase Activity Section ***
  on(PurchaseActivityActions.clearPurchaseActivitySection, (state, {tab}) => {
    const stateKey: PurchaseActivityStateKey | null = getStateKeyFromTab(tab);
    if (!stateKey) {
      return state;
    }

    return {
      ...state,
      [stateKey]: {
        pagination: { ...initialPagination },
        itemsPerPage: [],
        isLoaded: false,
      },
    };
  }),

  // *** Load Specific Purchase Activity Item in Section ***
  on(PurchaseActivityActions.loadSpecificPurchaseActivityItemInSectionSuccess, (state, {tab, response, page}) => {
    const stateKey: PurchaseActivityStateKey | null = getStateKeyFromTab(tab);
    if (!stateKey) {
      return state;
    }

    const currentSection: IPurchaseActivityList = state[stateKey];
    const itemsPerPage: IPurchaseActivityItem[][] = [...currentSection.itemsPerPage];
    const currentItems: IPurchaseActivityItem[] = itemsPerPage[page] || [];

    let updatedItems: IPurchaseActivityItem[];
    if (response.data[0]) {
      const index: number = currentItems.findIndex((i: IPurchaseActivityItem) => i.id === response.data[0].id);
      if (index !== -1) {
        updatedItems = currentItems.map((i: IPurchaseActivityItem, idx: number) => idx === index ? response.data[0] : i);
      } else {
        updatedItems = [...currentItems, response.data[0]];
      }
    } else {
      updatedItems = [];
    }

    itemsPerPage[page] = updatedItems;

    return {
      ...state,
      [stateKey]: {
        ...currentSection,
        itemsPerPage,
      },
    };
  }),
);

export function getStateKeyFromTab(tab: string): PurchaseActivityStateKey | null {
  switch (tab) {
    case EPurchaseActivityPreviousOrdersTabs.ORDERS:
      return 'previousOrders';
    case EPurchaseActivityPreviousOrdersTabs.ARCHIVE:
      return 'archiveOrders';
    case EPurchaseActivityTabs.PENDING_ORDERS:
      return 'pendingOrders';
    case EPurchaseActivityTabs.PREVIOUS_QUOTES:
      return 'previousQuotes';
    case EPurchaseActivityTabs.PREVIOUS_REQUESTS:
      return 'previousRequests';
    default:
      return null;
  }
}

export function PurchaseActivityReducer(state: IPurchaseActivityState | undefined, action: Action): IPurchaseActivityState {
  return reducer(state, action);
}

export const purchaseActivityBaseState: MemoizedSelector<object, IPurchaseActivityState> =
  createFeatureSelector<IPurchaseActivityState>('purchaseActivity');

export const selectPreviousOrders: MemoizedSelector<object, IPurchaseActivityList> = createSelector(
  purchaseActivityBaseState,
  state => state.previousOrders,
);

export const selectArchiveOrders: MemoizedSelector<object, IPurchaseActivityList> = createSelector(
  purchaseActivityBaseState,
  state => state.archiveOrders,
);

export const selectPendingOrders: MemoizedSelector<object, IPurchaseActivityList> = createSelector(
  purchaseActivityBaseState,
  state => state.pendingOrders,
);

export const selectPreviousQuotes: MemoizedSelector<object, IPurchaseActivityList> = createSelector(
  purchaseActivityBaseState,
  state => state.previousQuotes,
);

export const selectPreviousRequests: MemoizedSelector<object, IPurchaseActivityList> = createSelector(
  purchaseActivityBaseState,
  state => state.previousRequests,
);

export const selectPurchaseActivityDetails: MemoizedSelector<object, IPurchaseActivityItem | null> = createSelector(
  purchaseActivityBaseState,
  state => state.purchaseActivityDetails,
);

export const selectIsPurchaseActivityDetailsLoading: MemoizedSelector<object, boolean> = createSelector(
  purchaseActivityBaseState,
  state => state.isPurchaseActivityDetailsLoading,
);

export const selectPaginationByTab:
  (tab: string) => MemoizedSelector<object, IPurchaseActivityPaginationResponse | undefined> =
  (tab: string) => createSelector(
    purchaseActivityBaseState,
    state => {
      const stateKey: PurchaseActivityStateKey | null = getStateKeyFromTab(tab);
      return stateKey ? state[stateKey].pagination : undefined;
    },
  );
